import i18n from "i18next";
import { faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { PartConfigurationAttributeUpdateForm } from "src/components/views/erp/configurator/inspecting/PartConfigurationAttributeUpdateForm.tsx";
import { CatalogPartAttributesForm } from "src/components/views/erp/parts/catalogPart/CatalogPartAttributesForm.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ShopOrderBatchUpdateAttributesRow {
	shopOrderId: number;
	partId: number;
	catalogPartId: number | null | undefined;
	partConfigurationId: number | null | undefined;
	partIsConfigurable: boolean;
}

export interface ShopOrderBatchUpdateAttributesMenuButtonProps {
	shopOrderBatchId: number | null | undefined;
	row: ShopOrderBatchUpdateAttributesRow;
	allSelectedRows: ShopOrderBatchUpdateAttributesRow[];
	onPartAttributesUpdated?: () => Promise<unknown>;
}

/**
 * Updates configuration attributes when only configured shop orders are selected,
 * or catalog part attributes when a single non-configurable part is selected.
 */
export function ShopOrderBatchUpdateAttributesMenuButton({
	shopOrderBatchId,
	row,
	allSelectedRows,
	onPartAttributesUpdated,
}: ShopOrderBatchUpdateAttributesMenuButtonProps) {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const canManagePartAttributes =
		hasPermission(Permissions.Parts.CatalogParts.MANAGE) || hasPermission(Permissions.Parts.SiteParts.MANAGE);

	const catalogPartId = row.catalogPartId;
	const configurationIds: number[] = allSelectedRows.map((row) => row.partConfigurationId).filter((v) => v != null);
	const onlyConfigurationsSelected =
		configurationIds.length > 0 && configurationIds.length === allSelectedRows.length;
	const onlyNonConfigurablePartSelected =
		allSelectedRows.length === 1 && !row.partIsConfigurable && catalogPartId != null;

	if (onlyConfigurationsSelected && hasPermission(Permissions.Parts.PartConfigurations.MANAGE)) {
		return (
			<AsyncMenuButton
				label={i18n.t("update_attributes")}
				icon={faPenToSquare}
				onClick={() =>
					openFormOnDialog({
						openDialog,
						component: PartConfigurationAttributeUpdateForm,
						title: i18n.t("update_attributes"),
						size: "sm",
						agentContext: {
							key: defineAgentUiContextKey("erp.production.batchSelectionUpdateAttributes.formDialog"),
							title: i18n.t("update_attributes"),
							data: {
								shopOrderBatchId: shopOrderBatchId ?? null,
								shopOrderIds: allSelectedRows.map((row) => row.shopOrderId),
								configurationIds,
							},
						},
						props: {
							configurationIds: configurationIds,
						},
					})
				}
			/>
		);
	}

	if (canManagePartAttributes && onlyNonConfigurablePartSelected && catalogPartId != null) {
		return (
			<AsyncMenuButton
				label={i18n.t("update_attributes")}
				icon={faPenToSquare}
				onClick={() =>
					openFormOnDialog({
						openDialog,
						component: CatalogPartAttributesForm,
						title: i18n.t("update_attributes"),
						size: "sm",
						agentContext: {
							key: defineAgentUiContextKey(
								"erp.production.batchSelectionUpdatePartAttributes.formDialog",
							),
							title: i18n.t("update_attributes"),
							data: {
								shopOrderBatchId: shopOrderBatchId ?? null,
								shopOrderId: row.shopOrderId,
								partId: row.partId,
								catalogPartId,
							},
						},
						props: {
							catalogPartId,
							sitePartId: hasPermission(Permissions.Parts.CatalogParts.MANAGE) ? undefined : row.partId,
							showSharedPartDataWarning: true,
						},
						onSubmit: async () => onPartAttributesUpdated?.(),
					})
				}
			/>
		);
	}

	return null;
}
